import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { PageSpinner } from '@/components/Spinner';
import { RouteError } from '@/components/RouteError';
import PrivateRoute from './PrivateRoute';
import PublicLayout from './PublicLayout';

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

// Protected pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const TranslatePage = lazy(() => import('@/pages/translate/TranslatePage'));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage'));
const JobsPage = lazy(() => import('@/pages/jobs/JobsPage'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const GlossaryPage = lazy(() => import('@/pages/glossary/GlossaryPage'));
const ReviewPage = lazy(() => import('@/pages/review/ReviewPage'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const AuditPage = lazy(() => import('@/pages/audit/AuditPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<PageSpinner />}>{node}</Suspense>;
}

const ROUTER_BASENAME = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');

export const router = createBrowserRouter(
  [
    {
      path: '/auth',
      element: <PublicLayout />,
      errorElement: <RouteError />,
      children: [
        { index: true, element: withSuspense(<LoginPage />) },
        { path: 'login', element: withSuspense(<LoginPage />) },
        { path: 'register', element: withSuspense(<RegisterPage />) },
        { path: 'forgot-password', element: withSuspense(<ForgotPasswordPage />) },
        { path: 'reset-password', element: withSuspense(<ResetPasswordPage />) },
      ],
    },
    {
      path: '/',
      element: <PrivateRoute />,
      errorElement: <RouteError />,
      children: [
        { index: true, element: withSuspense(<HomePage />) },
        { path: 'translate', element: withSuspense(<TranslatePage />) },
        { path: 'documents', element: withSuspense(<DocumentsPage />) },
        { path: 'jobs', element: withSuspense(<JobsPage />) },
        { path: 'billing', element: withSuspense(<BillingPage />) },
        { path: 'analytics', element: withSuspense(<AnalyticsPage />) },
        { path: 'glossary', element: withSuspense(<GlossaryPage />) },
        { path: 'review', element: withSuspense(<ReviewPage />) },
        { path: 'notifications', element: withSuspense(<NotificationsPage />) },
        { path: 'settings', element: withSuspense(<SettingsPage />) },
        { path: 'admin', element: withSuspense(<AdminPage />) },
        { path: 'audit', element: withSuspense(<AuditPage />) },
      ],
    },
    {
      path: '*',
      element: withSuspense(<NotFoundPage />),
    },
  ],
  { basename: ROUTER_BASENAME || undefined },
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default AppRouter;
